import React, { FunctionComponent, memo, useCallback, useEffect, useState } from 'react'
import styles from './ParticipantsPicker.module.sass'
import Field from '../../../controls/Field/Field'
import Hints from '../../lists/Hints/Hints'
import { useSubject } from '../../../../utils/hooks'
import { fromQuery } from '../../../../utils/rx-operators'
import { apiRequests } from '../../../../http/config'
import { entityElem, entityToggle, eqEntity, getId } from '../../../../utils/entity-utils'
import { intersection, isEmpty, isNonEmpty } from 'fp-ts/es6/Array'
import { useSetState, useUpdateEffect } from 'react-use'
import Aside from '../../../etc/Aside/Aside'
import clsx from 'clsx'
import Group from '../../../etc/Group/Group'
import { Checkbox } from '../../../controls/Checkbox/Checkbox'
import UserPanel from '../../elements/UserElement/UserElement'
import { ID } from '../../../../types/UtilityTypes'
import Button from '../../../controls/Button/Button'
import ChevronDownIcon from 'files/chevron/down.svg'
import SearchIcon from 'files/search.svg'
import CheckIcon from 'files/check.svg'
import { setProp } from '../../../../utils/utils'
import { motion } from 'framer-motion'
import { UserWithDepartments } from '../../../../responses/users'
import { DepartmentWithUsers } from '../../../../responses/departments'
import Ellipsis from '../../../etc/Ellipsis/Ellipsis'
import { Classes } from '../../../../styles'

interface Props {
    className?: string,
    value?: UserWithDepartments[],
    onChange: (value: UserWithDepartments[]) => void,
}

const ParticipantsPicker: FunctionComponent<Props> = props => {
    const { value, onChange } = props
    const [search, setSearch] = useState('')
    const [openGroups, setOpenGroups] = useSetState<Record<ID, boolean>>({})
    const [list$, list] = useSubject<string, UserWithDepartments[]>([], fromQuery(query =>
        apiRequests.users.list({query})
    ))
    const [departments$, departments] = useSubject<string, DepartmentWithUsers[]>([], fromQuery(query =>
        apiRequests.dictionary.list({ params: { key: 'department' }, query: { ...query, with_users: true } })
    ))

    useUpdateEffect(() => list$.next(search), [search])
    useEffect(() => departments$.next(''), [departments$])

    const toggleUser = useCallback(
        (user: UserWithDepartments) => onChange(entityToggle(user)(value ?? [])),
        [value, onChange],
    )

    return <Aside className={clsx(props.className, styles.picker)}>
        <div className={styles.top}>
            <div className={styles.header}>
                <div className={styles.title}>Участники: &nbsp;</div>
                <div
                    className={styles.count}>{(props.value?.length ?? 0) > 0 ? `${props.value?.length} человек` : 'Нет'}</div>
            </div>
            <Hints
                showEmpty
                items={list}
                renderer={user => <UserPanel className={styles.popupUser} user={user}>
                    {entityElem(user, props.value ?? []) && <CheckIcon/>}
                </UserPanel>}
                itemToKey={getId}
                onMore={() => list$.next(search)}
                onToggle={open => open && isEmpty(list) && list$.next(search)}
                onClick={toggleUser}
            >
                {
                    ({ open, close }) => <Field
                        block
                        placeholder={'Найти сотрудника'}
                        value={search}
                        onChange={setSearch}
                        onFocus={open}
                        onBlur={close}
                        leftItem={<SearchIcon className={styles.inputIcon}/>}
                    />
                }
            </Hints>
        </div>
        {
            departments.map(department => {
                const open = openGroups[department.id]
                const includes = isNonEmpty(intersection(eqEntity)(value ?? [], department.users))
                const onClick = () => setOpenGroups(setProp(department.id, !open))
                return <div key={department.id}>
                    <Group
                        className={styles.group}
                        headerHeight={30}
                        header={<>
                            <div className={styles.overlay} onClick={onClick}/>
                            <div className={clsx(styles.dot, { [styles.includes]: includes })}/>
                            <Ellipsis className={styles.department}>{department.title}</Ellipsis>
                            <Button className={styles.button} onClick={onClick}>
                                <ChevronDownIcon className={clsx({ [Classes.flip]: open })}/>
                            </Button>
                        </>}
                    >
                        <motion.div
                            className={styles.content}
                            initial={{ height: 0 }}
                            animate={{ height: open ? 'auto' : 0 }}
                        >
                            {
                                department.users.map(user =>
                                    <UserPanel
                                        className={styles.user}
                                        key={user.id}
                                        user={user}
                                    >
                                        <Checkbox
                                            value={entityElem(user, value ?? [])}
                                            onChange={() => toggleUser(user)}
                                        />
                                    </UserPanel>
                                )
                            }
                        </motion.div>
                    </Group>
                </div>
            })
        }
    </Aside>
}

export default memo(ParticipantsPicker)